import { FC, ReactElement } from "react";
import { Box, Button, Dialog, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { tokens } from "@/tokens";

interface LogoutModalProps extends StandardModalProps {
  maxWidth: "xs" | "sm" | "md" | "lg" | "xl";
  noPadding?: boolean;
  onConfirm: () => void;
}

const LogoutConfirmModal: FC<LogoutModalProps> = ({
  open,
  onClose,
  maxWidth,
  onConfirm,
}): ReactElement => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${tokens.border.default}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, lineHeight: 1.3 }}>
              Logout
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              Are you sure you want to logout? You will need to sign in again to access your account.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: tokens.text.muted, "&:hover": { color: tokens.text.primary } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3,
        py: 2,
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.secondary,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            "&:hover": { borderColor: tokens.text.muted, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            bgcolor: tokens.status.danger,
            color: "#fff",
            boxShadow: "none",
            "&:hover": { bgcolor: "#dc2626", boxShadow: "none" },
          }}
        >
          Logout
        </Button>
      </Box>
    </Dialog>
  );
};

export default LogoutConfirmModal;
